import { useMemo } from 'react';

import { Combobox, Group, TextInput, useCombobox } from 'ui';
import { useDebouncedValue } from 'ui/Hooks';
import { IconSearch } from 'ui/Icons';

import { OnPlaceSelect, usePlacesAutoComplete } from '../hooks/usePlacesAutoComplete';

interface MapLocationSearchProps {
  onPlaceSelect: OnPlaceSelect;
}
interface AutoCompleteAsyncProps {
  data?: google.maps.places.AutocompletePrediction[] | null;
  onChange: (value: string) => void;
  value?: string | number | readonly string[] | undefined;
  onOptionSelect: (placeId: string) => void;
}

const AutoComplete = ({ data, onChange, value, onOptionSelect }: AutoCompleteAsyncProps) => {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const options = useMemo(
    () =>
      (data || []).map((item) => (
        <Combobox.Option value={item.description} key={item.place_id}>
          <Group wrap="nowrap">{item.description}</Group>
        </Combobox.Option>
      )),
    [data]
  );

  //Throttle the empty so that on first character search we don't show no results found
  const isEmpty = useMemo(() => Boolean(value && options.length == 0), [options?.length, value]);
  const [throttledIsEmpty] = useDebouncedValue(isEmpty, 400);

  return (
    <Combobox
      onOptionSubmit={(optionValue) => {
        onChange(optionValue);
        const place = data?.find((item) => item.description === optionValue);
        if (place) {
          onOptionSelect(place.place_id);
        }
        combobox.closeDropdown();
      }}
      withinPortal={true}
      store={combobox}
    >
      <Combobox.Target>
        <TextInput
          placeholder="Search city or zip code"
          value={value}
          onChange={(event) => {
            onChange(event.currentTarget.value);
            combobox.resetSelectedOption();
            combobox.openDropdown();
          }}
          onClick={() => combobox.openDropdown()}
          leftSection={<IconSearch />}
          onBlur={() => combobox.closeDropdown()}
        />
      </Combobox.Target>

      <Combobox.Dropdown hidden={!value || !options}>
        <Combobox.Options>
          {options}
          {throttledIsEmpty == true ? <Combobox.Empty>No results found</Combobox.Empty> : null}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};

const MapLocationSearch = ({ onPlaceSelect }: MapLocationSearchProps) => {
  const { predictionResults, onInputChange, inputValue, handleSuggestionClick } =
    usePlacesAutoComplete({
      onPlaceSelect: (place) => {
        onPlaceSelect(place);
      },
    });
  return (
    <AutoComplete
      data={predictionResults}
      onChange={onInputChange}
      value={inputValue}
      onOptionSelect={handleSuggestionClick}
    />
  );
};

export { MapLocationSearch };
